import React, { useState, useEffect } from 'react';
import './ManualExerciseEditor.css';

export default function ManualExerciseEditor({ exerciseType, initialExercise, defaultText, onSave, onCancel }) {
    // ── TRUE / FALSE STATE ──
    const [tfStatement, setTfStatement] = useState('');
    const [tfAnswer, setTfAnswer] = useState(true);

    // ── QUIZ STATE ──
    const [quizQuestion, setQuizQuestion] = useState('');
    const [quizChoices, setQuizChoices] = useState(['', '', '', '']);
    const [quizCorrect, setQuizCorrect] = useState(0);

    // ── FILL STATE ──
    const [fillTemplate, setFillTemplate] = useState('');
    const [fillBlanks, setFillBlanks] = useState([{ correct: '', distractors: '' }]);

    // ── WORDSELECT STATE ──
    const [wsText, setWsText] = useState('');
    const [wsTargets, setWsTargets] = useState('');

    // ── MATCH STATE ──
    const [matchPairs, setMatchPairs] = useState([{ left: '', right: '' }, { left: '', right: '' }]);

    // ── DRAGDROP STATE ──
    const [ddFullText, setDdFullText] = useState('');
    const [ddHiddenIndices, setDdHiddenIndices] = useState([]); // indices of hidden words
    const [ddDistractors, setDdDistractors] = useState(''); // comma separated

    // ── COMMON STATE ──
    const [explanation, setExplanation] = useState('');

    useEffect(() => {
        // Initialize based on type
        if (exerciseType === 'truefalse') {
            setTfStatement(initialExercise?.statement || defaultText || '');
            setTfAnswer(initialExercise?.answer !== undefined ? initialExercise.answer : true);
            setExplanation(initialExercise?.explanation || '');
        } else if (exerciseType === 'quiz') {
            setQuizQuestion(initialExercise?.question || defaultText || '');
            setQuizChoices(initialExercise?.choices?.length === 4 ? initialExercise.choices : ['Opcion 1', 'Opcion 2', 'Opcion 3', 'Opcion 4']);
            setQuizCorrect(initialExercise?.correct ?? 0);
            setExplanation(initialExercise?.explanation || '');
        } else if (exerciseType === 'fill') {
            setFillTemplate(initialExercise?.template || defaultText || 'El sol es una ____ muy brillante.');
            if (initialExercise?.blanks) {
                const bData = initialExercise.blanks.map((b, i) => {
                    const allChoices = initialExercise.choices?.[i] || [];
                    const distractors = allChoices.filter(c => c !== b).join(', ');
                    return { correct: b, distractors };
                });
                setFillBlanks(bData.length > 0 ? bData : [{ correct: '', distractors: '' }]);
            } else {
                setFillBlanks([{ correct: 'estrella', distractors: 'planeta, luna' }]);
            }
            setExplanation(initialExercise?.explanation || '');
        } else if (exerciseType === 'wordselect') {
            setWsText(initialExercise?.text || defaultText || '');
            setWsTargets((initialExercise?.targetWords || []).join(', '));
            setExplanation(initialExercise?.explanation || '');
        } else if (exerciseType === 'match') {
            setMatchPairs(initialExercise?.pairs || [{ left: '', right: '' }, { left: '', right: '' }]);
            setExplanation(initialExercise?.explanation || '');
        } else if (exerciseType === 'dragdrop') {
            if (initialExercise?.template && initialExercise?.correctWords) {
                let full = initialExercise.template;
                initialExercise.correctWords.forEach(w => {
                    full = full.replace('____', w);
                });
                setDdFullText(full);
                // Auto-detect hidden indices based on correctWords
                let tokens = full.split(/(\s+)/);
                let found = [];
                tokens.forEach((t, i) => {
                    if (i % 2 === 0) {
                        let clean = t.replace(/[.,!?:]/g, '');
                        if (initialExercise.correctWords.includes(clean) && !found.includes(i/2)) {
                            found.push(i/2);
                        }
                    }
                });
                setDdHiddenIndices(found.slice(0, initialExercise.correctWords.length));
            } else {
                setDdFullText(defaultText || 'El sol es una estrella muy brillante.');
                setDdHiddenIndices([]);
            }
            setDdDistractors((initialExercise?.distractors || []).join(', '));
            setExplanation(initialExercise?.explanation || '');
        }
    }, [exerciseType, initialExercise, defaultText]);

    const handleSave = () => {
        let payload = null;

        if (exerciseType === 'truefalse') {
            if (!tfStatement.trim()) return alert('La afirmación no puede estar vacía');
            payload = {
                type: 'truefalse',
                statement: tfStatement.trim(),
                answer: tfAnswer,
                explanation: explanation.trim()
            };
        } else if (exerciseType === 'quiz') {
            if (!quizQuestion.trim()) return alert('La pregunta no puede estar vacía');
            if (quizChoices.some(c => !c.trim())) return alert('Todas las opciones deben tener texto');
            payload = {
                type: 'quiz',
                question: quizQuestion.trim(),
                choices: quizChoices.map(c => c.trim()),
                correct: quizCorrect,
                explanation: explanation.trim()
            };
        } else if (exerciseType === 'fill') {
            if (!fillTemplate.trim() || !fillTemplate.includes('____')) return alert('La plantilla debe contener al menos un hueco "____"');
            
            const blanksCount = (fillTemplate.match(/____/g) || []).length;
            if (blanksCount !== fillBlanks.length) {
                return alert(`La plantilla tiene ${blanksCount} huecos pero hay ${fillBlanks.length} configurados. Ajusta los bloques de huecos.`);
            }

            for (let i = 0; i < fillBlanks.length; i++) {
                if (!fillBlanks[i].correct.trim()) return alert(`El hueco #${i+1} necesita una respuesta correcta.`);
            }

            const finalBlanks = fillBlanks.map(b => b.correct.trim());
            const finalChoices = fillBlanks.map(b => {
                const dists = b.distractors.split(',').map(d => d.trim()).filter(Boolean);
                // The correct answer must be in the choices array. We shuffle them below or in the player, but let's just include it.
                // The player logic might not shuffle them, so we just append. (Usually we'd shuffle, but here we just store).
                return [b.correct.trim(), ...dists];
            });

            payload = {
                type: 'fill',
                template: fillTemplate.trim(),
                blanks: finalBlanks,
                choices: finalChoices,
                explanation: explanation.trim()
            };
        } else if (exerciseType === 'wordselect') {
            if (!wsText.trim()) return alert('El texto no puede estar vacío');
            const targets = wsTargets.split(',').map(t => t.trim()).filter(Boolean);
            if (targets.length === 0) return alert('Debes especificar al menos una palabra clave');
            
            payload = {
                type: 'wordselect',
                text: wsText.trim(),
                targetWords: targets,
                explanation: explanation.trim()
            };
        } else if (exerciseType === 'match') {
            for (let i = 0; i < matchPairs.length; i++) {
                if (!matchPairs[i].left.trim() || !matchPairs[i].right.trim()) {
                    return alert(`El par #${i+1} está incompleto.`);
                }
            }
            payload = {
                type: 'match',
                pairs: matchPairs.map(p => ({ left: p.left.trim(), right: p.right.trim() })),
                explanation: explanation.trim()
            };
        } else if (exerciseType === 'dragdrop') {
            if (!ddFullText.trim()) return alert('El texto no puede estar vacío');
            if (ddHiddenIndices.length === 0) return alert('Debes ocultar al menos una palabra haciendo clic en ella.');
            
            const tokens = ddFullText.split(/(\s+)/);
            let correctWords = [];
            let templateTokens = tokens.map((tok, i) => {
                if (i % 2 === 0) {
                    const wordIdx = i / 2;
                    if (ddHiddenIndices.includes(wordIdx)) {
                        let cleanWord = tok.replace(/[.,!?:]/g, '');
                        let punct = tok.replace(/[^.,!?:]/g, '');
                        correctWords.push(cleanWord);
                        return "____" + punct;
                    }
                }
                return tok;
            });

            payload = {
                type: 'dragdrop',
                template: templateTokens.join(''),
                correctWords,
                distractors: ddDistractors.split(',').map(w => w.trim()).filter(Boolean),
                explanation: explanation.trim()
            };
        }

        if (payload) {
            onSave(payload);
        }
    };

    return (
        <div className="mee-root">
            <div className="mee-header">
                <span className="mee-title">
                    ✏️ {initialExercise ? 'Editar Ejercicio' : 'Crear Ejercicio Manual'}
                </span>
                <button className="mee-close" onClick={onCancel}>×</button>
            </div>

            {/* ── TRUE / FALSE ── */}
            {exerciseType === 'truefalse' && (
                <>
                    <div className="mee-field">
                        <label className="mee-label">Afirmación</label>
                        <textarea className="mee-textarea" value={tfStatement} onChange={e => setTfStatement(e.target.value)} />
                    </div>
                    <div className="mee-field">
                        <label className="mee-label">Respuesta Correcta</label>
                        <select className="mee-select" value={tfAnswer ? 'true' : 'false'} onChange={e => setTfAnswer(e.target.value === 'true')}>
                            <option value="true">Verdadero</option>
                            <option value="false">Falso</option>
                        </select>
                    </div>
                </>
            )}

            {/* ── QUIZ ── */}
            {exerciseType === 'quiz' && (
                <>
                    <div className="mee-field">
                        <label className="mee-label">Pregunta</label>
                        <textarea className="mee-textarea" value={quizQuestion} onChange={e => setQuizQuestion(e.target.value)} />
                    </div>
                    <div className="mee-field">
                        <label className="mee-label">Opciones y Respuesta Correcta</label>
                        {quizChoices.map((choice, i) => (
                            <div key={i} className="mee-choice-row">
                                <input 
                                    type="radio" 
                                    name="quiz_correct" 
                                    className="mee-choice-radio"
                                    checked={quizCorrect === i} 
                                    onChange={() => setQuizCorrect(i)} 
                                />
                                <span className="mee-choice-letter">{['A', 'B', 'C', 'D'][i]}</span>
                                <input 
                                    className="mee-input" 
                                    style={{ flex: 1 }} 
                                    value={choice} 
                                    onChange={e => {
                                        const n = [...quizChoices]; n[i] = e.target.value; setQuizChoices(n);
                                    }} 
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* ── FILL ── */}
            {exerciseType === 'fill' && (
                <>
                    <div className="mee-field">
                        <label className="mee-label">
                            Plantilla (Usa 4 guiones bajos "____" para los huecos)
                            <button 
                                style={{ background:'none', border:'none', color:'var(--brand)', cursor:'pointer', fontSize:11 }}
                                onClick={() => {
                                    const count = (fillTemplate.match(/____/g) || []).length;
                                    const newBlanks = Array.from({length: count}).map((_, i) => fillBlanks[i] || {correct:'', distractors:''});
                                    setFillBlanks(newBlanks);
                                }}
                            >
                                🔄 Sincronizar Huecos
                            </button>
                        </label>
                        <textarea className="mee-textarea" value={fillTemplate} onChange={e => setFillTemplate(e.target.value)} />
                    </div>
                    
                    {fillBlanks.map((b, i) => (
                        <div key={i} className="mee-blank-block">
                            <div className="mee-label" style={{color:'var(--brand)'}}>Hueco #{i+1}</div>
                            <div className="mee-field">
                                <label className="mee-label">Palabra Correcta</label>
                                <input className="mee-input" value={b.correct} onChange={e => {
                                    const n = [...fillBlanks]; n[i].correct = e.target.value; setFillBlanks(n);
                                }} />
                            </div>
                            <div className="mee-field">
                                <label className="mee-label">Distractores <span className="mee-hint">(Separados por coma)</span></label>
                                <input className="mee-input" placeholder="ej. azul, verde, rojo" value={b.distractors} onChange={e => {
                                    const n = [...fillBlanks]; n[i].distractors = e.target.value; setFillBlanks(n);
                                }} />
                            </div>
                        </div>
                    ))}
                </>
            )}

            {/* ── WORDSELECT ── */}
            {exerciseType === 'wordselect' && (
                <>
                    <div className="mee-field">
                        <label className="mee-label">Texto completo</label>
                        <textarea className="mee-textarea" value={wsText} onChange={e => setWsText(e.target.value)} />
                    </div>
                    <div className="mee-field">
                        <label className="mee-label">Palabras a encontrar <span className="mee-hint">(Separadas por coma)</span></label>
                        <input className="mee-input" placeholder="ej. mitochondria, nucleus, cell" value={wsTargets} onChange={e => setWsTargets(e.target.value)} />
                    </div>
                </>
            )}

            {/* ── MATCH ── */}
            {exerciseType === 'match' && (
                <>
                    <div className="mee-field">
                        <label className="mee-label">
                            Parejas (Término y Definición)
                            <button 
                                style={{ background:'none', border:'none', color:'var(--brand)', cursor:'pointer', fontSize:11, marginLeft: 10 }}
                                onClick={() => setMatchPairs([...matchPairs, { left: '', right: '' }])}
                            >
                                ＋ Añadir Pareja
                            </button>
                        </label>
                    </div>
                    {matchPairs.map((pair, i) => (
                        <div key={i} className="mee-blank-block" style={{ flexDirection: 'row', gap: 10 }}>
                            <div className="mee-field" style={{ flex: 1, marginTop: 0 }}>
                                <input className="mee-input" placeholder="Término" value={pair.left} onChange={e => {
                                    const n = [...matchPairs]; n[i].left = e.target.value; setMatchPairs(n);
                                }} />
                            </div>
                            <div className="mee-field" style={{ flex: 2, marginTop: 0 }}>
                                <input className="mee-input" placeholder="Definición" value={pair.right} onChange={e => {
                                    const n = [...matchPairs]; n[i].right = e.target.value; setMatchPairs(n);
                                }} />
                            </div>
                            <button 
                                style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:14 }}
                                onClick={() => {
                                    const n = matchPairs.filter((_, idx) => idx !== i);
                                    setMatchPairs(n.length ? n : [{left: '', right: ''}]);
                                }}
                            >✕</button>
                        </div>
                    ))}
                </>
            )}

            {/* ── DRAGDROP ── */}
            {exerciseType === 'dragdrop' && (
                <>
                    <div className="mee-field">
                        <label className="mee-label">Oración Completa</label>
                        <textarea className="mee-textarea" value={ddFullText} onChange={e => {
                            setDdFullText(e.target.value);
                            setDdHiddenIndices([]); // Reset when typing
                        }} />
                    </div>
                    <div className="mee-field">
                        <label className="mee-label">Haz clic en las palabras para ocultarlas:</label>
                        <div className="mee-clickable-text" style={{ padding: 10, background: 'rgba(255,255,255,0.05)', borderRadius: 6, lineHeight: 1.8 }}>
                            {ddFullText.split(/(\s+)/).map((tok, i) => {
                                if (i % 2 !== 0) return <span key={i}>{tok}</span>;
                                const wordIdx = i / 2;
                                const isHidden = ddHiddenIndices.includes(wordIdx);
                                return (
                                    <span 
                                        key={i} 
                                        onClick={() => setDdHiddenIndices(prev => prev.includes(wordIdx) ? prev.filter(x => x !== wordIdx) : [...prev, wordIdx])}
                                        style={{ 
                                            cursor: 'pointer', 
                                            padding: '2px 4px', 
                                            borderRadius: 4, 
                                            background: isHidden ? 'var(--brand)' : 'transparent',
                                            color: isHidden ? '#fff' : 'inherit',
                                            border: isHidden ? 'none' : '1px dashed rgba(255,255,255,0.3)',
                                            margin: '0 1px'
                                        }}
                                    >
                                        {tok}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                    <div className="mee-field">
                        <label className="mee-label">Distractores <span className="mee-hint">(Separados por coma)</span></label>
                        <input className="mee-input" placeholder="ej. luna, oscuro, planeta" value={ddDistractors} onChange={e => setDdDistractors(e.target.value)} />
                    </div>
                </>
            )}

            {/* ── COMMON EXPLANATION ── */}
            <div className="mee-field" style={{marginTop: 8}}>
                <label className="mee-label">Explicación (Feedback al alumno)</label>
                <textarea className="mee-textarea" style={{minHeight: 40}} value={explanation} onChange={e => setExplanation(e.target.value)} />
            </div>

            <div className="mee-footer">
                <button className="mee-btn mee-btn-cancel" onClick={onCancel}>Cancelar</button>
                <button className="mee-btn mee-btn-save" onClick={handleSave}>Guardar Ejercicio</button>
            </div>
        </div>
    );
}
